import moduleInformationActions from "actions/module_information";
import quoteActions from "actions/quote";
import { CloseableContent } from "dyl-components";
import React, { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Dropdown, Button, Popup, Header, Ref } from "semantic-ui-react";
import ConvertNewCustomerPopup from "shared/ConvertNewCustomerPopup";
import { ACTIONS, QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";
import ConvertAccountToCustomerForm from "shared/forms/ConvertAccountToCustomerForm";

import ConvertToNewCustomerForm from "shared/forms/ConvertToNewCustomerForm";
import ConvertToOpportunityForm from "shared/forms/ConvertToOpportunityForm";

import LostForm from "shared/forms/LostForm";
import RestrictedView from "shared/RestrictedView";
import { USER_PERMISSIONS } from "dyl-components";
import { PhoneUtil } from "utils";
import contactsActions from "actions/contacts";
import contactActions from "actions/contact";

const ConversionControls = ({record}) => {
    const {
        account_id,
        contact_id,
        canConvertToOpportunity,
        account_stage,
        master_source,
        secondary_source,
        current_primary_contact_id,
        contactName,
        account_name,
        account_is_a_customer,
        phone,
        email,
        mainLocation,
        dialer
    } = useSelector((state) => {
        const {
            module: account_stage,
            contact,
            master_source,
            secondary_source,
        } = state.module_information.pipeline;

        return {
            account_id: contact?.account?.id,
            contact_id: contact?.id,

            canConvertToOpportunity: account_stage === "lead",
            account_stage,

            master_source,
            secondary_source: secondary_source?.id || null,
            current_primary_contact_id: contact?.account?.primary_person_id,

            contactName: `${contact?.first_name} ${contact?.last_name} ${
                contact?.suffix || ""
            }`,
            account_name: contact?.account?.name,
            account_is_a_customer: contact?.account?.customer,

            phone: contact.phone,
            email: contact.email,
            mainLocation: contact?.location,
            dialer: state.officeView.dialer
        };
    });

    const navigate = useNavigate();
    const [params] = useSearchParams();
    const location = useLocation();
    const { contact_id: profileContactId } = useParams();
    const { campaign_id, contact_id: dialerContactId } = dialer || {};
    const isDialerRunning = PhoneUtil.isDialerRunning(dialer);
    const selectedCampaign = Number(dialerContactId) === Number(contact_id) ? campaign_id : undefined;

    const refresh = (pipeline_id) => {
        const query = new URLSearchParams(params);
        query.set("module_id", pipeline_id);
        const query_string = query.toString();
        if (profileContactId) {
            if (isDialerRunning) {
                dispatch(contactsActions.dialerViewLookup(profileContactId));
            }
            dispatch(contactActions.readContact(contact_id))
        }
        navigate(`${location.pathname}?module_id=${pipeline_id}`);
        navigate(
            `${location.pathname}${query_string ? `?${query_string}` : ""}`
        );
    };

    const modalRef = React.createRef();

    const [conversionModalOpen, setConversionModalOpen] = useState("");
    const [isConversionFormModified, setIsConversionFormModified] =
        useState(false);

    const [lostFormOpen, setLostFormOpen] = useState(false);

    const dispatch = useDispatch();

    const onClose = () => {
        setConversionModalOpen("");
        setIsConversionFormModified(false);
    };

    const onCloseLostForm = () => {
        setLostFormOpen(false);
    };

    const module_id = params.get("module_id");

    const recentOpportunityQuote = useSelector((state) => state.quote.recentOpportunityQuote);
    const canUpsell = account_stage !== 'lead';
    const isUpsellingFromQuote = canUpsell && (recentOpportunityQuote?.recent_inactive_quote_id || recentOpportunityQuote?.recent_active_quote_id);
    const isUpselling = useSelector((state) => {
        if (module_id) {
            return state.quote.opportunityBeingUpsold === Number(module_id);
        }
        return false;
    });

    const getPopupContent = () => {
        switch (conversionModalOpen) {
            case "opportunity":
                return (
                    <React.Fragment>
                        <Header color="primary">Convert to Opportunity</Header>
                        <ConvertToOpportunityForm
                            size="small"
                            account_id={account_id}
                            contact_id={contact_id}
                            onClose={onClose}
                            refresh={() => {
                                refresh(module_id);
                                dispatch(moduleInformationActions.getOne(module_id));
                            }}
                            onModify={(isModified) => {
                                setIsConversionFormModified(isModified);
                            }}
                            master_source={master_source}
                            secondary_source={secondary_source}
                            account_stage={account_stage}
                            campaign_id={selectedCampaign}
                        />
                    </React.Fragment>
                );
            case "customer":
                return (
                    <React.Fragment>
                        <Header color="primary">
                            Convert{" "}
                            <Popup
                                trigger={
                                    <span className="Convert__account-name">
                                        {account_name || contactName}
                                    </span>
                                }
                                content={account_name || contactName}
                                inverted
                            />{" "}
                            to Customer
                        </Header>
                        <ConvertAccountToCustomerForm 
                            current_primary_contact_id={current_primary_contact_id}
                            existing_account_id={account_id}
                            onClose={onClose}
                            onModify={(isModified) => {
                                setIsConversionFormModified(isModified);
                            }}
                            owner_id={contact_id}
                            pipeline_id={module_id}
                            refresh={() => {
                                refresh(module_id);
                            }}
                            size={"small"}
                            campaign_id={selectedCampaign}
                        />
                    </React.Fragment>
                );
            case "new-customer":
                return (
                    <React.Fragment>
                        <Header color="primary">
                            Create Customer-Account
                        </Header>
                        <ConvertToNewCustomerForm 
                            existing_account_id={account_id}
                            onClose={onClose}
                            onModify={(isModified) => {
                                setIsConversionFormModified(isModified);
                            }}
                            owner_id={contact_id}
                            pipeline_id={module_id}
                            refresh={() => {
                                dispatch(moduleInformationActions.getOne(module_id)).then(() => {
                                    onUpsell();
                                })
                            }}
                            size={"small"}
                            account_details={{
                                address: mainLocation,
                                email: email?.email,
                                email_type: email?.email_type,
                                phone: phone?.phone,
                                phone_type: phone?.phone_type
                            }}
                            campaign_id={selectedCampaign}
                        />
                    </React.Fragment>
                );
            default:
                return null;
        }
    };

    const isPrimaryContact = current_primary_contact_id === contact_id;
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const { onOpenAddProducts, onEditQuote } = useContext(QuoteBuilderContext);

    const recentActiveQuoteId = recentOpportunityQuote?.recent_active_quote_id;

    const onUpsell = () => {
        if (isUpsellingFromQuote) {
            onEditQuote(recentOpportunityQuote?.recent_inactive_quote_id || recentActiveQuoteId)
        } else {
            onOpenAddProducts({ isUpsell: true });
        }
    }

    return (
        <React.Fragment>
            <Popup
                content={
                    <CloseableContent onClose={onClose}>
                        {getPopupContent()}
                    </CloseableContent>
                }
                open={conversionModalOpen}
                position="bottom left"
                context={modalRef}
                closeOnDocumentClick={!isConversionFormModified}
                onClose={onClose}
                style={ !module_id ? { zIndex: 5 } : {}}
            />
            <Ref innerRef={modalRef}>
                <Dropdown
                    as={Button}
                    size="tiny"
                    text="Convert"
                    onOpen={() => {
                        setIsDropdownOpen(true)
                        if (module_id && canUpsell) {
                            dispatch(
                                quoteActions.getRecentQuoteByOpportunityId(Number(module_id), {
                                    account_id
                                })
                            );
                        }
                    }}
                    onClose={() => {setIsDropdownOpen(false)}}
                    pointing="top right"
                    loading={isUpselling}
                >
                    <Dropdown.Menu>
                        {!isUpselling && (
                            <>
                                {canConvertToOpportunity && (
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.OPPORTUNITY_EDIT} userRestrict assignmentIds={[(record?.contact?.assignees?.owner_id || 0), (record?.contact?.assignees?.coowner_id || 0)]}>
                                        <Dropdown.Item
                                            onClick={() => {
                                                setConversionModalOpen("opportunity");
                                            }}
                                        >
                                            Convert to Opportunity
                                        </Dropdown.Item>
                                    </RestrictedView>
                                )}
                                {!account_is_a_customer ? (
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(record?.contact?.account?.owner_id || 0), (record?.contact?.account?.coowner_id || 0)]}>
                                        <Dropdown.Item 
                                            style={{ width: 160 }} 
                                            onClick={() => {
                                                if (!recentActiveQuoteId) {
                                                    setConversionModalOpen(account_id ? 'customer' : 'new-customer');
                                                }
                                            }}
                                            {...(isUpsellingFromQuote ? ({ as: Dropdown, size: "tiny", trigger: "Convert Customer" }) : {})}
                                            text="Convert Customer"
                                        >
                                            {recentActiveQuoteId ? (
                                                <Dropdown.Menu>
                                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(record?.contact?.account?.owner_id || 0), (record?.contact?.account?.coowner_id || 0)]}>
                                                        <Dropdown.Item onClick={() => {onEditQuote(recentActiveQuoteId)}}>
                                                            Use Existing Quote
                                                        </Dropdown.Item>
                                                    </RestrictedView>
                                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(record?.contact?.account?.owner_id || 0), (record?.contact?.account?.coowner_id || 0)]}>
                                                        <Dropdown.Item 
                                                            onClick={() => {
                                                                setConversionModalOpen(account_id ? "customer" : "new-customer")
                                                            }}
                                                        >
                                                            Don't Use Existing Quote
                                                        </Dropdown.Item>
                                                    </RestrictedView>
                                                </Dropdown.Menu>
                                            ) : "Convert Customer"}
                                        </Dropdown.Item>
                                    </RestrictedView>
                                ) : (
                                    <>
                                        <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ORDERS_ADD} userRestrict>
                                            <Dropdown.Item disabled={account_stage === 'lead'} onClick={onUpsell}>
                                                Upsell Existing Customer
                                            </Dropdown.Item>
                                        </RestrictedView>
                                        <ConvertNewCustomerPopup 
                                            open={isDropdownOpen && (isPrimaryContact)}
                                            isPrimaryContact={isPrimaryContact}
                                            account_id={account_id}
                                            trigger={(
                                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(record?.contact?.account?.owner_id || 0), (record?.contact?.account?.coowner_id || 0)]}>
                                                    <Dropdown.Item
                                                        disabled={isPrimaryContact}
                                                        style={{ width: 195 }}
                                                        onClick={() => {
                                                            if (!recentActiveQuoteId) {
                                                                setConversionModalOpen("new-customer");
                                                            }
                                                        }}
                                                        {...(isUpsellingFromQuote ? ({ as: Dropdown, size: "tiny", trigger: "Convert New Customer" }) : {})}
                                                        text="Convert New Customer"
                                                    >
                                                        {recentActiveQuoteId ? (
                                                            <Dropdown.Menu>
                                                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(record?.contact?.account?.owner_id || 0), (record?.contact?.account?.coowner_id || 0)]}>
                                                                    <Dropdown.Item onClick={() => {
                                                                        onEditQuote(recentActiveQuoteId, ACTIONS.CONVERT_NEW_CUSTOMER);
                                                                    }}>
                                                                        Use Existing Quote
                                                                    </Dropdown.Item>
                                                                </RestrictedView>
                                                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(record?.contact?.account?.owner_id || 0), (record?.contact?.account?.coowner_id || 0)]}>
                                                                    <Dropdown.Item onClick={() => {setConversionModalOpen("new-customer")}}>
                                                                        Don't Use Existing Quote
                                                                    </Dropdown.Item>
                                                                </RestrictedView>
                                                            </Dropdown.Menu>
                                                        ) : "Convert New Customer"}
                                                    </Dropdown.Item>
                                                </RestrictedView>
                                            )}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </Ref>
            <RestrictedView type="popupDisabled" userPermission={record?.module === "opportunity" ? USER_PERMISSIONS.OPPORTUNITY_EDIT : USER_PERMISSIONS.LEAD_EDIT} userRestrict assignmentIds={[(record?.contact?.assignees?.owner_id || 0), (record?.contact?.assignees?.coowner_id || 0)]}>
                <Popup
                    trigger={
                            <Button size="tiny" color="red">
                                Lost
                            </Button>
                    }
                    content={
                        <CloseableContent onClose={onCloseLostForm}>
                            <LostForm
                                account_id={account_id}
                                account_stage={account_stage}
                                contact_id={contact_id}
                                pipeline_id={module_id}
                                refresh={(pipeline_id) => {
                                    dispatch(
                                        moduleInformationActions.getOne(pipeline_id)
                                    );
                                    refresh(pipeline_id);
                                }}
                            />
                        </CloseableContent>
                    }
                    on="click"
                    open={lostFormOpen}
                    position="bottom left"
                    onClose={() => {
                        if (!lostFormOpen) {
                            onCloseLostForm();
                        }
                    }}
                    onOpen={() => {
                        setLostFormOpen(true);
                    }}
                />
            </RestrictedView>

        </React.Fragment>
    );
};

export default ConversionControls;
